import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { api, formatError } from "@/lib/api";
import { LOGO, ZELLE_QR } from "@/lib/assets";
import { LogOut, Home, Receipt, GraduationCap, FolderOpen, Bell, BookOpen, Send, Shield, Megaphone } from "lucide-react";

const iconMap = { invoice: Receipt, grades: GraduationCap, projects: FolderOpen, reminders: Bell, homework: BookOpen };

export default function Parents() {
  const { t, lang, toggle } = useLang();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [broadcasts, setBroadcasts] = useState([]);

  useEffect(() => { api.get("/broadcasts").then((r) => setBroadcasts(r.data)).catch(() => {}); }, []);

  const doLogout = () => { logout(); navigate("/"); };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!msg.trim()) return;
    setSending(true);
    try {
      await api.post("/inquiries", {
        name: user?.name || user?.email || "Parent",
        email: user?.email || "parent@icdh.edu.ht",
        message: msg,
        type: "parent-message",
      });
      toast.success(t.contact.form.success);
      setMsg("");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setSending(false); }
  };

  const cards = [
    { key: "invoice", ...t.parents.invoice },
    { key: "grades", ...t.parents.grades },
    { key: "projects", ...t.parents.projects },
    { key: "reminders", ...t.parents.reminders },
    { key: "homework", ...t.parents.homework },
  ];

  return (
    <div className="min-h-screen bg-icdh-surface text-icdh-navy">
      {/* Header */}
      <header className="bg-icdh-navy text-icdh-white sticky top-0 z-40">
        <div className="mx-auto max-w-[1400px] px-6 py-4 flex items-center justify-between gap-4">
          <Link to="/" data-testid="parents-home" className="flex items-center gap-3">
            <img src={LOGO} alt="ICDH" className="h-10 w-10 object-contain bg-white rounded-full p-1" />
            <div className="leading-none">
              <p className="font-extrabold tracking-tight text-sm leading-tight">Institution Chrétienne d'Haïti</p>
              <p className="font-serif italic text-icdh-white/90 text-[11px]">{t.motto}</p>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <button onClick={toggle} data-testid="parents-lang" className="h-9 px-3 border border-icdh-white/30 text-xs font-bold tracking-[0.15em] hover:bg-icdh-white hover:text-icdh-navy transition-colors">
              {lang === "fr" ? "EN" : "FR"}
            </button>
            <Link to="/" data-testid="parents-view-site" className="text-sm flex items-center gap-2 hover:text-icdh-red transition-colors"><Home size={16} /><span className="hidden sm:inline">Home</span></Link>
            {user?.role === "admin" && (
              <Link to="/admin" data-testid="parents-admin-link" className="text-sm flex items-center gap-2 hover:text-icdh-red transition-colors">
                <Shield size={16} /><span className="hidden sm:inline">{t.parents.admin_link}</span>
              </Link>
            )}
            <button onClick={doLogout} data-testid="parents-logout" className="text-sm flex items-center gap-2 hover:text-icdh-red transition-colors">
              <LogOut size={16} /><span className="hidden sm:inline">{t.parents.logout}</span>
            </button>
          </div>
        </div>
      </header>

      {/* Hero strip */}
      <section className="bg-icdh-navy text-icdh-white pb-16 pt-4">
        <div className="mx-auto max-w-[1400px] px-6">
          <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="text-xs uppercase tracking-[0.3em] text-icdh-red">
            {t.parents.welcome}, {user?.name || user?.email}
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="mt-3 font-serif text-4xl md:text-5xl tracking-tight max-w-3xl">
            {t.parents.title}
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="mt-4 text-icdh-white/70 max-w-2xl">
            {t.parents.subtitle}
          </motion.p>
        </div>
      </section>

      <div className="mx-auto max-w-[1400px] px-6 py-12 grid lg:grid-cols-3 gap-8">
        {/* Zelle payment card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
          data-testid="zelle-card"
          className="lg:col-span-1 bg-icdh-white border border-icdh-navy/10 p-8 flex flex-col"
        >
          <p className="text-xs uppercase tracking-[0.28em] text-icdh-red">Zelle</p>
          <h2 className="mt-3 font-serif text-2xl md:text-3xl tracking-tight text-icdh-navy">{t.parents.pay_title}</h2>
          <p className="mt-3 text-icdh-navy/70 text-sm">{t.parents.pay_body}</p>
          <div className="mt-6 bg-icdh-surface border border-icdh-navy/10 flex items-center justify-center p-3">
            <img src={ZELLE_QR} alt="Zelle QR — Scan me" data-testid="zelle-qr" className="w-full max-w-[360px] h-auto object-contain" />
          </div>
          <p className="mt-4 text-center text-icdh-navy/70 text-xs uppercase tracking-[0.25em]">{t.parents.scan}</p>
        </motion.div>

        {/* Info cards */}
        <div className="lg:col-span-2 grid sm:grid-cols-2 gap-4">
          {cards.map((c, i) => {
            const Icon = iconMap[c.key];
            return (
              <motion.div
                key={c.key}
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 + i * 0.06 }}
                data-testid={`parent-card-${c.key}`}
                className="bg-icdh-white border border-icdh-navy/10 p-6 hover:border-icdh-red transition-colors duration-300"
              >
                <Icon className="text-icdh-red" size={22} />
                <h3 className="mt-4 font-sans font-bold text-icdh-navy tracking-tight">{c.t}</h3>
                <p className="mt-2 text-icdh-navy/60 text-sm">{c.d}</p>
                <p className="mt-4 text-xs uppercase tracking-[0.2em] text-icdh-navy/70">{t.parents.pending}</p>
              </motion.div>
            );
          })}
        </div>

        {/* Broadcasts / School notices */}
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55 }}
          data-testid="broadcasts-card"
          className="lg:col-span-3 bg-icdh-white border border-icdh-navy/10 p-6 md:p-8"
        >
          <div className="flex items-center gap-3">
            <Megaphone className="text-icdh-red" />
            <h3 className="font-serif text-2xl tracking-tight text-icdh-navy">{t.parents.broadcasts_title}</h3>
          </div>
          {broadcasts.length === 0 ? (
            <p className="mt-4 text-icdh-navy/60 text-sm" data-testid="broadcasts-empty">{t.parents.broadcasts_empty}</p>
          ) : (
            <div className="mt-6 grid gap-4">
              {broadcasts.map((b, i) => (
                <motion.article
                  key={b.id}
                  initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 + i * 0.05 }}
                  data-testid={`broadcast-${b.id}`}
                  className="border-l-2 border-icdh-red pl-5 py-2"
                >
                  <p className="text-xs uppercase tracking-[0.25em] text-icdh-red">
                    {t.parents.from_school} · {new Date(b.created_at).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US", { year: "numeric", month: "short", day: "numeric" })}
                  </p>
                  <h4 className="mt-2 font-sans font-bold text-icdh-navy tracking-tight">{b.title?.[lang] || b.title?.en || b.title?.fr}</h4>
                  <p className="mt-2 text-icdh-navy/75 text-sm whitespace-pre-wrap">{b.body?.[lang] || b.body?.en || b.body?.fr}</p>
                </motion.article>
              ))}
            </div>
          )}
        </motion.div>

        {/* Message form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}
          data-testid="parent-message-card"
          className="lg:col-span-2 bg-icdh-navy text-icdh-white p-8"
        >
          <p className="text-xs uppercase tracking-[0.28em] text-icdh-red">Contact</p>
          <h3 className="mt-3 font-serif text-2xl md:text-3xl tracking-tight">{t.parents.message_title}</h3>
          <form onSubmit={sendMessage} className="mt-6 flex flex-col md:flex-row gap-4">
            <textarea
              rows={3}
              data-testid="parent-message-input"
              placeholder={t.parents.message_placeholder}
              value={msg}
              onChange={(e) => setMsg(e.target.value)}
              className="flex-1 bg-transparent border border-icdh-white/25 focus:border-icdh-red outline-none p-4 text-icdh-white placeholder:text-icdh-white/40 resize-none"
            />
            <button
              type="submit"
              disabled={sending || !msg.trim()}
              data-testid="parent-message-send"
              className="h-14 md:self-end px-6 bg-icdh-red text-icdh-white text-xs font-bold uppercase tracking-[0.15em] hover:bg-icdh-white hover:text-icdh-navy transition-colors duration-300 disabled:opacity-60 flex items-center gap-2 justify-center"
            >
              <Send size={14} />{sending ? "..." : t.contact.form.submit}
            </button>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
